import {
  AlertColor,
  Box,
  DialogContent,
  DialogProps as MUIDialogProps,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import Button from "@common/components/Button";
import { ApiErrorResponse } from "@common/api";
import Typography from "../Typography";
import ConsoleLog from "@pages/Tickets/ConsoleLog";
import DialogTitle from "../Dialog/DialogTitle";
import DialogActions from "../Dialog/DialogActions";
import Dialog from "../Dialog";
import { NotificationContext } from "../Notification";

export type DialogProps = MUIDialogProps & {
  severity: AlertColor;
  title: string;
  message: string;
  details?: ApiErrorResponse;
  onClose: any;
  closeButtonLabel?: string;
};

const DialogAlert = (props: DialogProps) => {
  const { t } = useTranslation();
  const {
    severity,
    title,
    message,
    details,
    onClose,
    closeButtonLabel = "Close",
    ...other
  } = props;
  const { dispatchNotification } = useContext(NotificationContext);

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(details?.data.error ?? message);
      onClose();
      dispatchNotification({
        title: t("Success"),
        message: t("Error has been successfully copied to the clipboard."),
        severity: "success",
      });
    } catch {
      dispatchNotification({
        title: t("Error"),
        message: t(
          "An error occured while copying this error to your clipboard."
        ),
      });
    }
  };

  return (
    <Dialog
      {...other}
      maxWidth="sm"
      fullWidth
      data-testid="error-details-dialog"
    >
      <DialogTitle severity={severity}>{title}</DialogTitle>
      <DialogContent>
        <>
          <Box
            sx={{
              marginBottom: 2,
            }}
          >
            <Typography data-testid="error-info">{message}</Typography>
          </Box>
          {details && (
            <Box sx={{ wordBreak: "break-all" }}>
              <Divider sx={{ my: 2 }} />

              <Grid
                container
                direction="column"
                spacing={1.25}
                sx={{ width: "100%" }}
              >
                <Grid>
                  <Grid container spacing={2} sx={{ width: "100%" }}>
                    <Grid size={2}>
                      <ConsoleLog>
                        <Typography realTime data-testid="error-label">
                          {t("Error")}
                        </Typography>
                      </ConsoleLog>
                    </Grid>
                    <Grid size={10}>
                      <Typography
                        realTime
                        sx={{
                          paddingLeft: 2,
                          color: "common.grey.gBFBFBF",
                        }}
                        data-testid="error-value"
                      >
                        {details?.data.error}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid>
                  <Grid container spacing={2} sx={{ width: "100%" }}>
                    <Grid size={2}>
                      <ConsoleLog>
                        <Typography realTime data-testid="error-message-label">
                          {t("Message")}
                        </Typography>
                      </ConsoleLog>
                    </Grid>
                    <Grid size={10}>
                      <Typography
                        realTime
                        sx={{
                          paddingLeft: 2,
                          color: "common.grey.gBFBFBF",
                        }}
                        data-testid="error-message-value"
                      >
                        {details?.data.message}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid>
                  <Grid container spacing={2} sx={{ width: "100%" }}>
                    <Grid size={2}>
                      <ConsoleLog>
                        <Typography realTime data-testid="error-code-label">
                          {t("Error Code")}
                        </Typography>
                      </ConsoleLog>
                    </Grid>
                    <Grid size={10}>
                      <Typography
                        realTime
                        sx={{
                          paddingLeft: 2,
                          color: "common.grey.gBFBFBF",
                        }}
                        data-testid="error-code-value"
                      >
                        {details?.data.errorCode}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </>
      </DialogContent>
      <DialogActions>
        {details && (
          <Button
            variant="outlined"
            onClick={handleClick}
            color="white"
            data-testid="copy-error-button"
          >
            Copy Error
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onClose}
          color="white"
          data-testid="close-button"
        >
          {closeButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogAlert;
